import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {Trade} from "../utils/typesTrading";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableSortLabel from "@mui/material/TableSortLabel";
import CodeMirror from "@uiw/react-codemirror";
import {python} from "@codemirror/lang-python";
import {vscodeDark} from "@uiw/codemirror-theme-vscode";

type SortDir = 'asc' | 'desc';
type TradeKey = keyof Trade;

interface TradeHistoryTableProps {
    trades: Trade[];
    code?: string;
}

export const TradeHistoryTable: React.FC<TradeHistoryTableProps> = ({ trades, code }) => {
    const [orderBy, setOrderBy] = React.useState<TradeKey>('trade_date');
    const [order, setOrder] = React.useState<SortDir>('asc');

    const handleSort = (col: TradeKey) => {
        if (orderBy === col) {
            setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(col);
            setOrder('asc');
        }
    };

    const sorted = React.useMemo(() => {
        return [...trades].sort((a, b) => {
            const av = a[orderBy];
            const bv = b[orderBy];
            if (av == null) return 1;
            if (bv == null) return -1;
            const cmp = av < bv ? -1 : av > bv ? 1 : 0;
            return order === 'asc' ? cmp : -cmp;
        });
    }, [trades, orderBy, order]);

    const COLUMNS: { key: TradeKey; label: string; align: 'left' | 'right' }[] = [
        { key: 'trade_date', label: 'Date',     align: 'left' },
        { key: 'action',     label: 'Action',   align: 'left' },
        { key: 'ticker',     label: 'Ticker',   align: 'left' },
        { key: 'quantity',   label: 'Quantity', align: 'right' },
        { key: 'price',      label: 'Price',    align: 'right' },
    ];

    return (
        <Box sx={{ mt: 4, width: '100%', maxWidth: 1600 }}>
            {code && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Code</Typography>
                    <CodeMirror
                        value={code}
                        theme={vscodeDark}
                        extensions={[python()]}
                        editable={false}
                        readOnly={true}
                        style={{ fontSize: 13 }}
                    />
                </Box>
            )}
            <Typography variant="h6" sx={{ mb: 1 }}>Trade History ({trades.length} trades)</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            {COLUMNS.map(({ key, label, align }) => (
                                <TableCell key={key} align={align} sortDirection={orderBy === key ? order : false} sx={{ fontWeight: 700 }}>
                                    <TableSortLabel
                                        active={orderBy === key}
                                        direction={orderBy === key ? order : 'asc'}
                                        onClick={() => handleSort(key)}
                                    >
                                        {label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map((trade, idx) => (
                            <TableRow key={idx} hover sx={{
                                backgroundColor: trade.action?.toUpperCase().includes('BUY')
                                    ? 'rgba(56, 142, 60, 0.08)'
                                    : trade.action?.toUpperCase().includes('SELL')
                                        ? 'rgba(211, 47, 47, 0.08)'
                                        : undefined
                            }}>
                                <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
                                <TableCell>
                                    {trade.trade_date
                                        ? new Date(trade.trade_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
                                        : 'N/A'}
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 600,
                                    color: trade.action?.toUpperCase().includes('BUY')
                                        ? 'success.main'
                                        : trade.action?.toUpperCase().includes('SELL')
                                            ? 'error.main'
                                            : undefined
                                }}>
                                    {trade.action}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{trade.ticker}</TableCell>
                                <TableCell align="right">{trade.quantity.toFixed(4)}</TableCell>
                                <TableCell align="right">
                                    {trade.price != null
                                        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(trade.price)
                                        : 'N/A'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};